<template>
  <div :class="$style.filters">
    <course-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.courses)"
      :model-value="modelValue.courses"
      :class="$style.filter"
      :disabled="disabled"
      @update:model-value="onFiltersChange($event, 'courses')"
    />
    <e-learning-license-status-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.licenseStatuses)"
      :model-value="modelValue.licenseStatuses"
      :class="$style.filter"
      @update:model-value="onFiltersChange($event, 'licenseStatuses')"
    />
    <attendance-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.attendanceStatuses)"
      :model-value="modelValue.attendanceStatuses"
      :class="$style.filter"
      @update:model-value="onFiltersChange($event, 'attendanceStatuses')"
    />
    <course-status-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.courseStatuses)"
      :model-value="modelValue.courseStatuses"
      :class="$style.filter"
      :disabled="disabled"
      @update:model-value="onFiltersChange($event, 'courseStatuses')"
    />
    <package-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.packages)"
      :model-value="modelValue.packages"
      :package-types="packageTypes"
      :class="$style.filter"
      @update:model-value="onFiltersChange($event, 'packages')"
    />
    <form-item
      v-if="shouldShowFilter(TABLE_FILTERS.userAccountStatus)"
      :class="$style.filter"
    >
      <user-account-status-single-lookup
        :model-value="modelValue.userAccountStatus"
        density="compact"
        @update:model-value="onFiltersChange($event, 'userAccountStatus')"
      />
    </form-item>

    <form-item
      v-if="shouldShowFilter(TABLE_FILTERS.visibility)"
      :class="$style.filter"
    >
      <visibility-single-lookup
        :model-value="modelValue.visibility"
        density="compact"
        :placeholder="`${globalTranslate('assignedCourse.hiddenUnhidden')}`"
        @update:model-value="onFiltersChange($event, 'visibility')"
      />
    </form-item>
    <form-item
      v-if="shouldShowFilter(TABLE_FILTERS.expirationStatuses)"
      :class="$style.filter"
    >
      <expiration-status-multiple-lookup
        :model-value="modelValue.expirationStatuses"
        :disabled="disabled"
        @update:model-value="onFiltersChange($event, 'expirationStatuses')"
      />
    </form-item>

    <package-category-multiple-lookup
      v-if="shouldShowFilter(TABLE_FILTERS.packageCategories)"
      :model-value="modelValue.packageCategories"
      :class="$style.filter"
      :disabled="disabled"
      @update:model-value="onFiltersChange($event, 'packageCategories')"
    />

    <form-item
      :class="$style.filter"
    >
      <v-text-field
        :model-value="modelValue.searchString"
        hide-details
        density="compact"
        append-inner-icon="fas fa-solid fa-search"
        class="search-field"
        :placeholder="searchPlaceholder || globalTranslate('search')"
        :disabled="disabled"
        @update:model-value="onFiltersChange($event, 'searchString')"
      />
    </form-item>
  </div>
</template>

<script setup>
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { debounce } from '@pfsc/core/utils/debounce';
import FormItem from '../form-item.vue';
import AttendanceMultipleLookup from '../lookup/attendance-status-multiple-lookup.vue';
import CourseMultipleLookup from '../lookup/course-multiple-lookup.vue';
import CourseStatusMultipleLookup from '../lookup/course-status-multiple-lookup.vue';
import eLearningLicenseStatusMultipleLookup from '../lookup/e-learning-license-status-multiple-lookup.vue';
import PackageMultipleLookup from '../lookup/package-multiple-lookup.vue';
import UserAccountStatusSingleLookup from '../lookup/user-status-single-lookup.vue';
import VisibilitySingleLookup from '../lookup/visibility-single-lookup.vue';
import ExpirationStatusMultipleLookup from '../lookup/expiration-status-multiple-lookup.vue';
import PackageCategoryMultipleLookup from '../lookup/package-category-multiple-lookup.vue';
import { TABLE_FILTERS } from './table-filters-options';

const props = defineProps({
  filtersToShown: {
    type: [Array, String],
    default: TABLE_FILTERS.all,
  },
  packageTypes: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Object,
  },
  searchPlaceholder: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['update:model-value']);

const debouncedUpdateModelValue = debounce(async (value, field) => {
  emits('update:model-value', { ...props.modelValue, [field]: value });
});

const debouncedSearchUpdateModelValue = debounce(async (value, field) => {
  emits('update:model-value', { ...props.modelValue, [field]: value });
}, 1000);

/**
 * Handle filters change.
 * @param {unknown} value Value to update.
 * @param {string} field Field to update.
 */
function onFiltersChange(value, field) {
  const processedValue = field === 'searchString' && typeof value === 'string' ? value.trim() : value;
  if (field === 'searchString') {
    debouncedSearchUpdateModelValue(processedValue, field);
    return;
  }
  debouncedUpdateModelValue(value, field);
}

/**
 * Check to show filter.
 * @param {string} filter Filter to check.
 * @returns {boolean}
 */
function shouldShowFilter(filter) {
  if (
    typeof props.filtersToShown === 'string'
    && props.filtersToShown === TABLE_FILTERS.all
  ) {
    return true;
  }
  return props.filtersToShown.includes(filter);
}
</script>
<style scoped>
  ::v-deep(.v-field) {
    background: white;
    box-shadow: none;
    border: 1px solid var(--border-color-light);
    color: var(--button-text-light);
  }
</style>
<style lang="scss" module>
.filters {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.filter {
  flex: 1;
  min-width: max-content;

  label {
    gap: 0;
  }
  .v-field {
    box-shadow: none;
  }
}

@media screen and (max-width: 1104px) {
  .filters {
    gap: 10px;
  }
  .filter {
    flex: 0 1 calc(50% - 5px);
  }
}

@media screen and (max-width: 600px) {
  .filter {
    flex: 0 1 100%;
  }
}
</style>
